// components/admin/AdminChatPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  TextField,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Drawer,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Fab,
  BottomNavigation,
  BottomNavigationAction,
  Popover,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  SupportAgent as SupportIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  AccessTime as AccessTimeIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ExitToApp as ExitToAppIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useAdminChat } from '../context/AdminChatContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const AdminChatPanel = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  
  const {
    activeChats,
    selectedChat,
    messages,
    loading,
    isTyping,
    unreadCount,
    messagesEndRef,
    fetchActiveChats,
    fetchChatDetails,
    sendMessage,
    assignChat,
    closeChat,
    getUserInfo,
    setSelectedChat,
    markChatAsRead
  } = useAdminChat();

  const [newMessage, setNewMessage] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const locale = language === 'ar' ? ar : enUS;
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  const handleSelectChat = async (chat) => {
    setSelectedChat(chat);
    await fetchChatDetails(chat.id);
    
    if (chat.user_id || chat.session_id) {
      const info = await getUserInfo(chat.user_id, chat.session_id);
      setUserInfo(info);
    }
    
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    try {
      await sendMessage(selectedChat.id, newMessage);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleAssignChat = async () => {
    if (!selectedChat) return;
    await assignChat(selectedChat.id);
    setAnchorEl(null);
    setMenuAnchorEl(null);
    setBottomSheetOpen(false);
  };

  const handleCloseChat = async () => {
    if (!selectedChat) return;
    await closeChat(selectedChat.id);
    setSelectedChat(null);
    setUserInfo(null);
    setAnchorEl(null);
    setMenuAnchorEl(null);
    setBottomSheetOpen(false);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchActiveChats();
    setRefreshing(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'waiting': return '#FFD700';
      case 'closed': return '#9E9E9E';
      default: return '#FFD700';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  const handleMenuOpen = (event) => {
    if (isMobile) {
      setBottomSheetOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAnchorEl(null);
    setBottomSheetOpen(false);
  };

  // Mobile Bottom Sheet for Options
  const MobileOptionsSheet = () => (
    <Dialog
      open={bottomSheetOpen}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          margin: 0,
          width: '100%',
          backgroundColor: '#0D0D0D',
          borderTop: '2px solid #FFD700',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          maxHeight: 'auto'
        }
      }}
    >
      <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid #2D2D2D' }}>
        <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>
          {language === 'ar' ? 'خيارات المحادثة' : 'Chat Options'}
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        {selectedChat && !selectedChat?.assigned_to && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleAssignChat}
            startIcon={<AssignmentTurnedInIcon />}
            sx={{
              mb: 2,
              borderColor: '#FFD700',
              color: '#FFD700',
              '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' },
              py: 1.5
            }}
          >
            {language === 'ar' ? 'تسجيل المحادثة' : 'Assign to me'}
          </Button>
        )}
        {selectedChat && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCloseChat}
            startIcon={<ExitToAppIcon />}
            sx={{
              borderColor: '#FF3D00',
              color: '#FF3D00',
              '&:hover': { backgroundColor: 'rgba(255, 61, 0, 0.1)' },
              py: 1.5
            }}
          >
            {language === 'ar' ? 'إنهاء المحادثة' : 'Close chat'}
          </Button>
        )}
        <Button
          fullWidth
          onClick={handleMenuClose}
          sx={{
            mt: 2,
            color: '#B3B3B3',
            py: 1
          }}
        >
          {language === 'ar' ? 'إلغاء' : 'Cancel'}
        </Button>
      </Box>
    </Dialog>
  );

  // Mobile Header - Always Visible
  const MobileHeader = () => (
    <Box sx={{ 
      backgroundColor: '#0D0D0D',
      borderBottom: '1px solid #2D2D2D',
      position: 'sticky',
      top: 0,
      zIndex: 10
    }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setMobileDrawerOpen(true)} sx={{ color: '#FFD700' }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
            {language === 'ar' ? 'المحادثات' : 'Chats'}
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small"
                sx={{ ml: 1, backgroundColor: '#FF3D00', color: '#FFFFFF' }}
              />
            )}
          </Typography>
        </Box>
        <Tooltip title={language === 'ar' ? 'تحديث' : 'Refresh'}>
          <IconButton onClick={handleRefresh} disabled={refreshing} size="small">
            <RefreshIcon sx={{ color: refreshing ? '#6B6B6B' : '#FFD700', fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Selected Chat Info Bar - Shows even when no chat selected */}
      <Box sx={{ 
        px: 2, 
        pb: 2,
        borderTop: selectedChat ? '1px solid #2D2D2D' : 'none',
        display: selectedChat ? 'flex' : 'none',
        alignItems: 'center',
        gap: 2
      }}>
        {selectedChat && (
          <>
            <Avatar sx={{ bgcolor: '#FFD700', color: '#000000', width: 40, height: 40 }}>
              {getInitials(selectedChat.user_name)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 700 }}>
                {selectedChat.user_name || 'Guest'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {formatDate(selectedChat.created_at)}
              </Typography>
            </Box>
            <IconButton onClick={handleMenuOpen} sx={{ color: '#FFD700' }}>
              <MoreVertIcon />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );

  // Desktop Header - Always Visible
  const DesktopHeader = () => (
    <Box sx={{ 
      p: 2, 
      borderBottom: '1px solid #2D2D2D',
      backgroundColor: '#0D0D0D',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
          {language === 'ar' ? 'المحادثات' : 'Chats'}
          {unreadCount > 0 && (
            <Chip 
              label={unreadCount} 
              size="small"
              sx={{ ml: 1, backgroundColor: '#FF3D00', color: '#FFFFFF' }}
            />
          )}
        </Typography>
        {selectedChat && (
          <>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: '#2D2D2D' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: '#FFD700', color: '#000000', width: 32, height: 32 }}>
                {getInitials(selectedChat.user_name)}
              </Avatar>
              <Typography variant="subtitle1" sx={{ color: '#FFD700', fontWeight: 600 }}>
                {selectedChat.user_name || 'Guest'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {formatDate(selectedChat.created_at)}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {selectedChat && !selectedChat.assigned_to && (
          <Tooltip title={language === 'ar' ? 'تسجيل المحادثة' : 'Assign to me'}>
            <IconButton onClick={handleAssignChat} sx={{ color: '#FFD700' }}>
              <AssignmentIcon />
            </IconButton>
          </Tooltip>
        )}
        {selectedChat && (
          <Tooltip title={language === 'ar' ? 'إنهاء المحادثة' : 'Close chat'}>
            <IconButton onClick={handleCloseChat} sx={{ color: '#FF3D00' }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={language === 'ar' ? 'تحديث' : 'Refresh'}>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <RefreshIcon sx={{ color: refreshing ? '#6B6B6B' : '#FFD700' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  // Mobile Chat List Component
  const ChatList = () => (
    <Paper sx={{ 
      width: isMobile ? '100%' : 320,
      height: isMobile ? '100%' : 'auto',
      backgroundColor: '#0D0D0D',
      border: '1px solid #2D2D2D',
      borderRadius: isMobile ? 0 : '16px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
      
    }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #2D2D2D', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && (
            <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: '#FFD700', display: { md: 'none' } }}>
              <ChevronLeftIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
            {language === 'ar' ? 'المحادثات النشطة' : 'Active Chats'}
            {unreadCount > 0 && (
              <Chip 
                label={unreadCount} 
                size="small"
                sx={{ ml: 1, backgroundColor: '#FF3D00', color: '#FFFFFF' }}
              />
            )}
          </Typography>
        </Box>
        <Tooltip title={language === 'ar' ? 'تحديث' : 'Refresh'}>
          <IconButton onClick={handleRefresh} disabled={refreshing} size="small">
            <RefreshIcon sx={{ color: refreshing ? '#6B6B6B' : '#FFD700', fontSize: isMobile ? 20 : 24 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Tabs 
        value={tabValue} 
        onChange={(e, v) => setTabValue(v)} 
        sx={{ 
          borderBottom: '1px solid #2D2D2D',
          '& .MuiTab-root': {
            minWidth: isMobile ? 'auto' : 90,
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }
        }}
      >
        <Tab 
          label={language === 'ar' ? 'الكل' : 'All'} 
          sx={{ color: tabValue === 0 ? '#FFD700' : '#B3B3B3' }}
        />
        <Tab 
          label={language === 'ar' ? 'غير مقروء' : 'Unread'} 
          sx={{ color: tabValue === 1 ? '#FFD700' : '#B3B3B3' }}
        />
        <Tab 
          label={language === 'ar' ? 'خاصة بي' : 'Mine'} 
          sx={{ color: tabValue === 2 ? '#FFD700' : '#B3B3B3' }}
        />
      </Tabs>

      <List sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
        {activeChats
          .filter(chat => {
            if (tabValue === 1) return chat.unread_count > 0;
            if (tabValue === 2) return chat.assigned_to === user?.id;
            return true;
          })
          .map((chat) => (
            <ListItem
              key={chat.id}
              button
              selected={selectedChat?.id === chat.id}
              onClick={() => handleSelectChat(chat)}
              sx={{
                borderRadius: '12px',
                mb: 0.5,
                backgroundColor: selectedChat?.id === chat.id ? '#1A1A1A' : 'transparent',
                '&:hover': { backgroundColor: '#1A1A1A' },
                border: chat.unread_count > 0 ? '1px solid #FFD700' : 'none',
                p: isMobile ? 1 : 2
              }}
            >
              <ListItemAvatar>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(chat.status),
                        border: '2px solid #0D0D0D'
                      }}
                    />
                  }
                >
                  <Avatar sx={{ bgcolor: '#FFD700', color: '#000000', width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }}>
                    {getInitials(chat.user_name)}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: chat.unread_count > 0 ? 700 : 400, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                      {chat.user_name || 'Guest'}
                    </Typography>
                    {chat.unread_count > 0 && (
                      <Chip 
                        label={chat.unread_count} 
                        size="small"
                        sx={{ backgroundColor: '#FFD700', color: '#000000', height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" sx={{ color: '#B3B3B3', display: 'block', fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                      {chat.messages?.[0]?.message || 'No messages yet'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B6B6B', fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
                      {formatDate(chat.last_message_at)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        
        {activeChats.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: '#B3B3B3', fontSize: isMobile ? '0.875rem' : '1rem' }}>
              {language === 'ar' ? 'لا توجد محادثات نشطة' : 'No active chats'}
            </Typography>
          </Box>
        )}
      </List>
    </Paper>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' },
      height: { xs: 'calc(100vh - 70px)', md: 'calc(100vh - 100px)' },
      backgroundColor: '#000000', 
      p: { xs: 0, sm: 2 },
      gap: { xs: 0, sm: 2 },
      py: 4, mb: 10
    }}>
      {/* Desktop Chat List */}
      {!isMobile && <ChatList />}

      {/* Mobile Chat List Drawer */}
      {isMobile && (
        <SwipeableDrawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          onOpen={() => setMobileDrawerOpen(true)}
          PaperProps={{
            sx: {
              width: '85%',
              maxWidth: 320,
              backgroundColor: '#0D0D0D',
              borderRadius: 0
            }
          }}
        >
          <ChatList />
        </SwipeableDrawer>
      )}

      {/* Chat Area */}
      <Paper sx={{ 
        flex: 1, 
        backgroundColor: '#0D0D0D',
        border: { xs: 'none', sm: '1px solid #2D2D2D' },
        borderRadius: { xs: 0, sm: '16px' },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: { xs: '100%', md: 'auto' }
      }}>
        {/* Header - Always visible */}
        {isMobile ? <MobileHeader /> : <DesktopHeader />}

        {/* Messages Area */}
        {selectedChat ? (
          <>
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              p: { xs: 1.5, sm: 2 },
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}>
              {messages.map((msg, index) => {
                const isAdmin = msg.sender_type === 'admin';
                
                return (
                  <Box
                    key={msg.id || index}
                    sx={{
                      display: 'flex',
                      justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                      mb: 1
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: { xs: '85%', sm: '70%' },
                        backgroundColor: isAdmin ? '#FFD700' : '#1A1A1A',
                        color: isAdmin ? '#000000' : '#FFFFFF',
                        borderRadius: '12px',
                        p: { xs: 1, sm: 1.5 },
                        borderBottomRightRadius: isAdmin ? '4px' : '12px',
                        borderBottomLeftRadius: isAdmin ? '12px' : '4px'
                      }}
                    >
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {msg.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'right',
                          mt: 0.5,
                          color: isAdmin ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                          fontSize: { xs: '0.65rem', sm: '0.7rem' }
                        }}
                      >
                        {formatTime(msg.created_at)}
                        {msg.is_read && isAdmin && (
                          <CheckCircleIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              
              {isTyping && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Chip
                    label={language === 'ar' ? 'العميل يكتب...' : 'Customer is typing...'}
                    size="small"
                    sx={{
                      backgroundColor: '#1A1A1A',
                      color: '#B3B3B3',
                      animation: 'pulse 1.5s infinite',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }}
                  />
                </Box>
              )}
              
              <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Box
              component="form"
              onSubmit={handleSendMessage}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderTop: '1px solid #2D2D2D',
                display: 'flex',
                gap: 1
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={language === 'ar' ? 'اكتب ردك...' : 'Type your reply...'}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                InputProps={{
                  sx: {
                    color: '#FFFFFF',
                    backgroundColor: '#1A1A1A',
                    borderRadius: '20px',
                    '& fieldset': { borderColor: '#2D2D2D' },
                    '&:hover fieldset': { borderColor: '#FFD700' },
                    '& input': { fontSize: { xs: '0.875rem', sm: '1rem' }, py: { xs: 1, sm: 1.2 } }
                  }
                }}
              />
              <IconButton
                type="submit"
                disabled={!newMessage.trim()}
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  '&:hover': { backgroundColor: '#FFE55C' },
                  '&:disabled': { backgroundColor: '#2D2D2D', color: '#6B6B6B' }
                }}
              >
                <SendIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            flex: 1,
            color: '#B3B3B3',
            p: 3
          }}>
            <ChatIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#2D2D2D', mb: 2 }} />
            <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ textAlign: 'center' }}>
              {language === 'ar' ? 'اختر محادثة للبدء' : 'Select a chat to start'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Desktop Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #2D2D2D',
            color: '#FFFFFF',
            borderRadius: '12px',
            minWidth: 180
          }
        }}
      >
        {selectedChat && !selectedChat?.assigned_to && (
          <MenuItem onClick={handleAssignChat}>
            <AssignmentIcon sx={{ mr: 1, color: '#FFD700', fontSize: 20 }} />
            <Typography variant="body2">
              {language === 'ar' ? 'تسجيل المحادثة' : 'Assign to me'}
            </Typography>
          </MenuItem>
        )}
        {selectedChat && (
          <MenuItem onClick={handleCloseChat}>
            <CloseIcon sx={{ mr: 1, color: '#FF3D00', fontSize: 20 }} />
            <Typography variant="body2">
              {language === 'ar' ? 'إنهاء المحادثة' : 'Close chat'}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Mobile Bottom Sheet for Options */}
      <MobileOptionsSheet />

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
};

export default AdminChatPanel;